import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { ACCOUNT_LABELS, type AccountKind, type Contact, type ContactFinance as ContactFinanceData } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import { accountPath } from './AccountsPage';

/**
 * Schnellaktionen „Als Debitor anlegen“ und „Als Kreditor anlegen“ oben auf der Kontaktkarte. Sie legen das Personenkonto mit einem Klick an und vergeben die Nummer. Hat der Kontakt das Konto schon,
 * steht an der Stelle des Knopfs ein Link zur Maske des Kontos: Das Anlegen lässt sich nicht wiederholen. Auch der Server legt nie ein zweites an (eindeutiger Index je Kontakt und Art, die Anlage
 * läuft unter Sperre des Kontakts), selbst bei einem Doppelklick oder zwei offenen Fenstern. Gezeigt wird ein Knopf nur mit dem Zusatzrecht „Finanzdaten“ und dem Modul der Art
 * (Ausgangsrechnungen für Debitoren, Eingangsrechnungen für Kreditoren).
 */
export function ContactAccountButtons({ contact }: { contact: Contact }) {
  const { can, hasModule } = useSession();
  const client = useQueryClient();
  const allowed = can('contacts.finance');
  const finance = useQuery({
    queryKey: ['contact-finance', contact.id],
    queryFn: async () => (await api<{ data: ContactFinanceData }>('GET', `/contacts/${contact.id}/finance`)).data,
    enabled: allowed,
  });
  const create = useMutation({
    mutationFn: async (kind: AccountKind) => (await api<{ data: ContactFinanceData }>('POST', `/contacts/${contact.id}/finance/number`, { kind })).data,
    onSuccess: (d) => {
      client.setQueryData(['contact-finance', contact.id], d);
      client.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
  if (!allowed || !finance.data) return null;
  const data = finance.data;
  const error = create.error instanceof ApiError ? create.error.message : create.error ? 'Das Konto konnte nicht angelegt werden.' : null;

  const button = (kind: AccountKind, number: string | null, id: string | null) => {
    const names = ACCOUNT_LABELS[kind];
    if (!hasModule(kind === 'debtor' ? 'invoices_out' : 'invoices_in')) return null;
    if (number && id) {
      return (
        <Link key={kind} className="btn btn--secondary" to={`${accountPath(kind)}/${id}`} title={`Dieser Kontakt ist bereits ${names.singular}. Zur Maske des Kontos.`}>
          {names.singular} {number}
        </Link>
      );
    }
    return (
      <button key={kind} type="button" className="btn btn--secondary" disabled={create.isPending} onClick={() => create.mutate(kind)}>
        {create.isPending && create.variables === kind ? 'Wird angelegt …' : `Als ${names.singular} anlegen`}
      </button>
    );
  };

  const buttons = [button('debtor', data.debtorNumber, data.debtorId), button('creditor', data.creditorNumber, data.creditorId)].filter(Boolean);
  if (buttons.length === 0) return null;
  return (
    <>
      {buttons}
      {error && <div className="alert alert--error contact-quick__error" role="alert">{error}</div>}
    </>
  );
}
